import React, { useState, useEffect } from 'react';
import './CheckoutModal.css';

/** Utility to match the formatDate from ListingDetail.js */
const formatDate = (dateString) => {
  if (!dateString) {
    return '';
  }
  try {
    const options = {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
    };
    const formattedDate = new Intl.DateTimeFormat('en-US', options).format(new Date(dateString));
    const [date, timeWithAmpm] = formattedDate.split(', ');
    const [time, ampm] = timeWithAmpm.split(' ');
    return `${date.replace(/\//g, '-')} ${time} ${ampm.toLowerCase()} EST`;
  } catch (error) {
    console.error('Failed to format date:', error);
    return '';
  }
};

const loadPayPalScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    const clientid = process.env.REACT_APP_PAYPAL_CLIENT_ID;
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientid}&currency=USD&components=buttons&enable-funding=card&disable-funding=paylater&intent=authorize`;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const CheckoutModal = ({ listing: propListing, onExpand, isExpanded }) => {
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flavorTextDisplay, setFlavorTextDisplay] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  // Detect mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 600);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (propListing) {
      setListing(propListing);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [propListing]);

  useEffect(() => {
    if (listing?.cert?.card?.flavor_text) {
      let i = 0;
      const text = listing.cert.card.flavor_text;
      const interval = setInterval(() => {
        setFlavorTextDisplay((prev) => prev + text[i]);
        i++;
        if (i === text.length) clearInterval(interval);
      }, 30);
      return () => clearInterval(interval);
    }
  }, [listing]);

  useEffect(() => {
    const renderPayPalButtons = async () => {
      const loaded = await loadPayPalScript();
      if (loaded && window.paypal && listing) {
        try {
          window.paypal.Buttons({
            createOrder: (data, actions) => {
              return actions.order.create({
                purchase_units: [{
                  amount: {
                    value: listing.price.toFixed(2),
                  },
                }],
              });
            },
            onApprove: (data, actions) => {
              return actions.order.capture().then(function(details) {
                alert('Transaction completed by ' + details.payer.name.given_name);
              });
            },
            onError: (err) => {
              console.error('PayPal Checkout Error:', err);
            },
            style: {
              color: 'blue'
            }
          }).render('#paypal-button-container');

        } catch (err) {
          console.error('PayPal render error:', err);
        }
      }
    };

    if (listing) {
      renderPayPalButtons();
    }
  }, [listing]);

  // Add click handler for the container
  useEffect(() => {
    const container = document.getElementById('paypal-button-container');
    if (!container) return;

    const handleClick = (e) => {
      // Never expand on mobile
      if (isMobile) {
        console.log('PayPal expansion blocked on mobile');
        return;
      }
      
      // Check if the click was on or inside a card-related element
      if (e.target.closest('[data-funding-source="card"]')) {
        console.log('Card button clicked');
        if (onExpand) onExpand(true);
      }
    };

    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, [onExpand, isMobile]);

  if (loading) return null;
  if (error) return <p>Error loading listing: {error}</p>;
  if (!listing) return <p></p>;

  const { cert, price, cert: { card } } = listing;
  const frontImageUrl = cert?.cert_images?.front_image_url || 'https://via.placeholder.com/200';
  const certNumber = cert?.cert_number;
  const grade = cert?.grade || 'N/A';
  const name = card?.name || cert?.name || 'Unknown';
  const cardSet = card?.set || {};
  const illustrator = card?.artist || 'Unknown';

  const setInfo = cardSet.series === cardSet.name
    ? cardSet.name
    : `${cardSet.series || ''}: ${cardSet.name || ''}`;

  return (
    <div className={`checkout-modal ${isExpanded ? 'expanded' : ''}`}>
      <div className={`checkout-content ${isExpanded ? 'expanded' : ''}`}>
        <div className="checkout-left">
          <div className="name-grade">
            <div className="name-container">
            <h1><i>{name}</i></h1>
              {cert?.promo?.display_name && (
                <p className="promo-name"><i>{cert.promo.display_name}</i></p>
              )}
            </div>
            <span className="grade">{grade}</span>
          </div>

          <div className="details-row">
            <div className="set-and-illustrator">
              <div className="set-info">{setInfo}</div>
              <p className="illustrator">
                Illustrated by{' '}
                <a href={`/artists/${illustrator}`} className="illustrator-link">
                  {illustrator}
                </a>
              </p>
            </div>

            <div className="pricecharting-container">
              <div
                className="pricecharting-widget"
                onClick={() =>
                  cert?.pricing_data && cert?.price_charting_id
                    ? window.open(
                        `https://www.pricecharting.com/game/${cert.price_charting_id}`,
                        '_blank'
                      )
                    : null
                }
                style={{ cursor: cert?.pricing_data && cert?.price_charting_id ? 'pointer' : 'default', position: 'relative' }}
              >
                <h3>PriceCharting</h3>
                {cert?.pricing_data ? (
                  <>
                    <div className="price-row">
                      <span className="label">PSA 10</span>
                      <span className="colon">:</span>
                      <span className="price">
                        {cert.pricing_data.psa_10
                          ? `$${cert.pricing_data.psa_10 % 1 === 0
                              ? cert.pricing_data.psa_10.toFixed(0)
                              : cert.pricing_data.psa_10.toFixed(2)}`
                          : '--'}
                      </span>
                    </div>
                    <div className="price-row">
                      <span className="label">PSA 9</span>
                      <span className="colon">:</span>
                      <span className="price">
                        {cert.pricing_data.psa_9
                          ? `$${cert.pricing_data.psa_9 % 1 === 0
                              ? cert.pricing_data.psa_9.toFixed(0)
                              : cert.pricing_data.psa_9.toFixed(2)}`
                          : '--'}
                      </span>
                    </div>
                    <div className="price-row">
                      <span className="label">PSA 8</span>
                      <span className="colon">:</span>
                      <span className="price">
                        {cert.pricing_data.psa_8
                          ? `$${cert.pricing_data.psa_8 % 1 === 0
                              ? cert.pricing_data.psa_8.toFixed(0)
                              : cert.pricing_data.psa_8.toFixed(2)}`
                          : '--'}
                      </span>
                    </div>
                    <div className="price-row">
                      <span className="label">PSA 7</span>
                      <span className="colon">:</span>
                      <span className="price">
                        {cert.pricing_data.psa_7
                          ? `$${cert.pricing_data.psa_7 % 1 === 0
                              ? cert.pricing_data.psa_7.toFixed(0)
                              : cert.pricing_data.psa_7.toFixed(2)}`
                          : '--'}
                      </span>
                    </div>
                    {cert.pricing_data?.date_updated && (
                      <div className="date-updated">
                        {formatDate(cert.pricing_data.date_updated)}
                      </div>
                    )}
                  </>
                ) : (
                  <p>No pricing data available</p>
                )}
              </div>
            </div>
          </div>

          {card?.flavor_text && (
            <div className="flavor-text-box">
              <p className="flavor-text">
                <i>{flavorTextDisplay}</i>
              </p>
            </div>
          )}

          <div className="checkout-lower-section">
            <div className="price-details">
              <p>Price <span>${price.toFixed(2)}</span></p>
              <hr />
              <p className="total">Total <span>${price.toFixed(2)}</span></p>
            </div>
            <div className="paypal-container" id="paypal-button-container"></div>
          </div>
        </div>

        <div className="checkout-right">
          <div className="card-image-container">
            <a href={`https://www.psacard.com/cert/${certNumber}/psa`} target="_blank" rel="noopener noreferrer">
              <img src={frontImageUrl} alt={name} className="checkout-card-image" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;
